/**
 * API client configuration for FastAPI backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Generic API call function with error handling
 */
export async function apiCall<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
  body?: any,
  opts?: { timeoutMs?: number; retries?: number; retryDelayMs?: number }
): Promise<ApiResponse<T>> {
  const timeoutMs = opts?.timeoutMs ?? 15 * 60 * 1000; // default 15 minutes
  const maxRetries = opts?.retries ?? 2; // retry twice on network errors
  const retryDelayMs = opts?.retryDelayMs ?? 2000;

  // Get user email from localStorage or window
  const userEmail = localStorage.getItem('userEmail') || window.userEmail || '';

  let attempt = 0;
  while (true) {
    attempt++;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add email header if available
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }

      const options: RequestInit = {
        method,
        headers,
        signal: controller.signal,
      };

      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
      clearTimeout(timer);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      clearTimeout(timer);
      const isNetworkError = error?.name === 'AbortError' || error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError');
      const canRetry = isNetworkError && attempt <= maxRetries;
      console.error(`API call failed (attempt ${attempt}/${maxRetries + 1}):`, error);
      if (canRetry) {
        await delay(retryDelayMs * attempt); // simple backoff
        continue;
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  }
}

/**
 * Add a delay for demo purposes (remove in production)
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
